import {
  AdvancedPagination,
  Card,
  Dropdown,
  RadixTable,
  TColumn,
  DateRangePicker,
} from '@features/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DateValueType } from '@lib/utils.ts';
import { CalendarIcon } from '@features/ui/icons';
import { TOption } from '@src/types/dropdown.ts';
import { TPackages } from '@src/types/packages.ts';
import { ProductAccountTypes } from '@src/types/productsAddons.ts';
import { TReportLeaderBoardRow } from '@src/types/reports.ts';
import { getProductsAccountsOnboarding } from '@src/services/products.ts';
import { getLeaderboard } from '@src/services/leaderboard.ts';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

const ACCOUNT_TYPE_OPTIONS: TOption[] = [
  {
    label: 'Practice Account (qualification path)',
    value: ProductAccountTypes.PRACTICE_ACCOUNT,
  },
  {
    label: 'Straight to Performance Account (STPA)',
    value: ProductAccountTypes.STRAIGHT_TO_PA,
  },
  {
    label: 'Specialty Accounts (prebuilt)',
    value: ProductAccountTypes.SPECIALTY_ACCOUNT,
  },
];

export const LeaderboardPage = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [valueDate, setValueDate] = useState<DateValueType>(null);
  const [selectedAccountType, setSelectedAccountType] = useState<string | null>(
    null
  );
  const [sizePackages, setSizePackages] = useState<TPackages[]>([]);
  const [selectedProductId, setSelectedProductId] = useState<number | null>(
    null
  );
  const [tableData, setTableData] = useState<TReportLeaderBoardRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedAccountType) {
      setSizePackages([]);
      setSelectedProductId(null);

      return;
    }

    getProductsAccountsOnboarding({
      datafeed: 'rithmic_data_feed',
      account_types: [selectedAccountType as ProductAccountTypes],
    })
      .then((res) => setSizePackages(res.data))
      .catch(() => setSizePackages([]));
  }, [selectedAccountType]);

  // Fetch leaderboard data
  const fetchLeaderboard = useCallback(async () => {
    setIsLoading(true);
    try {
      if (!selectedProductId || !valueDate?.startDate || !valueDate?.endDate) {
        setTableData([]);
        setTotalItems(0);
        setTotalPages(0);
        setIsLoading(false);
        return;
      }
      const res = await getLeaderboard({
        product_id: selectedProductId,
        from_date: dayjs(valueDate.startDate).toISOString(),
        to_date: dayjs(valueDate.endDate).toISOString(),
        page: currentPage,
        page_size: pageSize,
      });

      setTableData(res.data);
      setTotalItems(res.total);
      setTotalPages(res.total_pages);
    } catch {
      setTableData([]);
      setTotalItems(0);
      setTotalPages(0);
      toast.error('Failed to fetch leaderboard data. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, selectedProductId, valueDate]);

  useEffect(() => {
    fetchLeaderboard();
  }, [fetchLeaderboard]);

  const handleAccountTypeChange = (option: TOption) => {
    setSelectedAccountType(option.value);
    setSelectedProductId(null);
    setCurrentPage(1);
  };

  const handleAccountSizeChange = (option: TOption) => {
    setSelectedProductId(Number(option.value));
    setCurrentPage(1);
  };

  const handleDateChange = (val: DateValueType) => {
    setValueDate(val);
    setCurrentPage(1);
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  };

  // Build dropdown options for account sizes
  const accountSizeOptions = useMemo((): TOption[] => {
    return sizePackages
      .filter((pkg) => pkg.id !== undefined)
      .map((pkg) => ({
        label: pkg.display_name_web ?? pkg.title ?? String(pkg.id),
        value: String(pkg.id),
      }));
  }, [sizePackages]);

  const getBgColor = useCallback((index?: number) => {
    switch (index) {
      case 0:
        return 'bg-[#FFEB67] text-[#262626]';
      case 1:
        return 'bg-[#93C7F9] text-[#262626]';
      case 2:
        return 'bg-[#FFD174] text-[#262626]';
      default:
        return '';
    }
  }, []);

  const columns = useMemo(
    (): TColumn<TReportLeaderBoardRow>[] => [
      {
        title: 'Rank',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>#{item?.rank}</p>
          </div>
        ),
      },
      {
        title: 'User',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.user_display_name}</p>
          </div>
        ),
      },
      {
        title: 'Username',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.user_name}</p>
          </div>
        ),
      },
      {
        title: 'Account',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.account}</p>
          </div>
        ),
      },
      {
        title: 'Profit',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.profit}</p>
          </div>
        ),
      },
      {
        title: 'Balance',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.balance}</p>
          </div>
        ),
      },
      {
        title: 'Trades',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.trades}</p>
          </div>
        ),
      },
      {
        title: 'Win %',
        className: 'h-[49px] py-0 px-0',
        render: (item, index) => (
          <div className={`h-full flex items-center px-2 ${getBgColor(index)}`}>
            <p>{item?.win_rate}%</p>
          </div>
        ),
      },
    ],
    [getBgColor]
  );

  return (
    <div className={'flex flex-col min-w-[900px] gap-y-3.5'}>
      <h1 className={'text-[23px] font-medium'}>Leaderboard</h1>
      <div className={'flex items-center justify-between mt-1'}>
        <DateRangePicker
          rightIcon={<CalendarIcon />}
          value={valueDate}
          onChange={handleDateChange}
        />
        <div className={'flex items-center gap-x-5'}>
          <Dropdown
            options={ACCOUNT_TYPE_OPTIONS}
            placeHolder="Select Account"
            onPress={handleAccountTypeChange}
          />
          <Dropdown
            options={accountSizeOptions}
            placeHolder="Select Account Size"
            onPress={handleAccountSizeChange}
            className={
              !selectedAccountType ? 'opacity-50 pointer-events-none' : ''
            }
          />
        </div>
      </div>
      <Card className={'items-end'}>
        {isLoading ? (
          <div
            className={'w-full flex justify-center py-10 text-muted-foreground'}
          >
            Loading...
          </div>
        ) : (
          <RadixTable columns={columns} data={tableData} />
        )}
        <AdvancedPagination
          className="mt-10"
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={pageSize}
          totalItems={totalItems}
          onPageChange={setCurrentPage}
          onPageSizeChange={handlePageSizeChange}
          pageSizeOptions={[10, 25, 50, 100]}
        />
      </Card>
    </div>
  );
};
